import React from 'react';
import {
    ContactSupportOutlined,
    Edit,
    ForumOutlined,
    NewReleasesOutlined,
    Schedule
} from '@mui/icons-material';
import { useTranslations } from 'next-intl';
import { ResponsiveStyleValue } from '@mui/system';
import { Property } from 'csstype';

import { InstitutionType, Match, MatchingResultContactStatus, MatchStatus } from '../../types';
import { WrapContainer } from '../../styles';
import { DigitalHubChip } from '../index';
import { useTheme, useUser } from '../../hooks';
import { differenceInCalendarDays } from 'date-fns';

interface Props {
    match: Pick<Match, 'status' | 'matchedInstitutions' | 'endDate'>;
    viewer?: InstitutionType;
    children?: React.ReactNode;
    alignSelf?: ResponsiveStyleValue<Property.AlignSelf>;
}

export const TagDisplay = ({
    match: { status, matchedInstitutions, endDate },
    viewer,
    children,
    alignSelf
}: Props) => {
    const t = useTranslations('matching');
    const { colors } = useTheme();
    const { user } = useUser();

    const isActive = status === MatchStatus.Active;
    const tags = [];
    const viewerIsCompany = viewer === InstitutionType.Company;

    if (!viewerIsCompany) {
        // These tags are only shown for creator institutions
        if (status === MatchStatus.Draft)
            tags.push(
                <DigitalHubChip
                    key={`tag_${tags.length}`}
                    label={t('match.state.Draft')}
                    icon={<Edit />}
                    backgroundColor={colors.surfaceVariant}
                />
            );
        else if (matchedInstitutions.length > 0) {
            if (matchedInstitutions.some((m) => m.isNew))
                tags.push(
                    <DigitalHubChip
                        key={`tag_${tags.length}`}
                        label={t('match.state.NewMatches')}
                        backgroundColor={colors.secondaryContainer}
                        icon={<NewReleasesOutlined />}
                    />
                );
            else
                tags.push(
                    <DigitalHubChip
                        key={`tag_${tags.length}`}
                        label={t('match.state.Matches')}
                        backgroundColor={colors.primaryContainer}
                    />
                );
        } else {
            tags.push(
                <DigitalHubChip
                    key={`tag_${tags.length}`}
                    label={t('match.state.NoMatches')}
                    backgroundColor={colors.errorContainer}
                />
            );
        }
    } else {
        // A company user should only match where she is matched and the match belongs to the company
        const matchResult = matchedInstitutions.find(
            ({ institution }) => institution.documentId === user?.institution?.documentId
        );
        if (!matchResult || matchResult.contactStatus === MatchingResultContactStatus.NoContact) {
            return null;
        }

        // These tags are only shown for matched institutions
        if (matchResult?.contactStatus === MatchingResultContactStatus.ContactingAllowed)
            tags.push(
                <DigitalHubChip
                    key={`tag_${tags.length}`}
                    label={t('contact.state.ContactAllowed')}
                    backgroundColor={colors.tertiaryContainer}
                    icon={<ContactSupportOutlined />}
                />
            );
        if (matchResult?.contactStatus === MatchingResultContactStatus.CommentingAllowed)
            tags.push(
                <DigitalHubChip
                    key={`tag_${tags.length}`}
                    label={t('contact.state.CommentingAllowed')}
                    backgroundColor={colors.tertiaryContainer}
                    icon={<ForumOutlined />}
                />
            );
        if (matchResult?.contactStatus === MatchingResultContactStatus.Full) {
            tags.push(
                <DigitalHubChip
                    key={`tag_${tags.length}`}
                    label={t('contact.state.CommentingAllowed')}
                    backgroundColor={colors.tertiaryContainer}
                    icon={<ForumOutlined />}
                />
            );
            tags.push(
                <DigitalHubChip
                    key={`tag_${tags.length}`}
                    label={t('contact.state.ContactAllowed')}
                    backgroundColor={colors.tertiaryContainer}
                    icon={<ContactSupportOutlined />}
                />
            );
        }

    }

    // Remaining time is displayed for matching creators and for matched institutions
    if (isActive && endDate) {
        const remainingDays = differenceInCalendarDays(endDate, new Date());
        tags.push(
            <DigitalHubChip
                key={`tag_${tags.length}`}
                label={t('remaining_time', { days: remainingDays })}
                icon={<Schedule />}
            />
        );
    }

    return (
        <WrapContainer gap={2} alignSelf={alignSelf}>
            {[...tags, children]}
        </WrapContainer>
    );
};
